<template>
  <div>
    <div class="overflow-hidden rounded border border-border-subtle focus-within:border-border-strong">
      <!-- Invisible overlay to close table picker on outside click -->
      <div v-if="showTablePicker" class="fixed inset-0 z-10" @click="closeTablePicker" />

      <!-- Main toolbar -->
      <div class="flex items-center gap-0.5 border-b border-border-subtle bg-surface-muted px-1.5 py-1">
        <button
          type="button"
          class="rounded p-1.5 text-text-muted hover:bg-surface-sunken hover:text-text-primary"
          :class="{ 'bg-surface-sunken text-text-primary': activeStates.bold }"
          :title="$t('W: Fed')"
          @click="editor?.chain().focus().toggleBold().run()">
          <IconLucideBold class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="rounded p-1.5 text-text-muted hover:bg-surface-sunken hover:text-text-primary"
          :class="{ 'bg-surface-sunken text-text-primary': activeStates.italic }"
          :title="$t('W: Kursiv')"
          @click="editor?.chain().focus().toggleItalic().run()">
          <IconLucideItalic class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-border-subtle" />
        <button
          type="button"
          class="rounded p-1.5 text-text-muted hover:bg-surface-sunken hover:text-text-primary"
          :class="{ 'bg-surface-sunken text-text-primary': activeStates.heading }"
          :title="$t('W: Overskrift')"
          @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()">
          <IconLucideHeading2 class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-border-subtle" />
        <button
          type="button"
          class="rounded p-1.5 text-text-muted hover:bg-surface-sunken hover:text-text-primary"
          :class="{ 'bg-surface-sunken text-text-primary': activeStates.bulletList }"
          :title="$t('W: Punktliste')"
          @click="editor?.chain().focus().toggleBulletList().run()">
          <IconLucideList class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="rounded p-1.5 text-text-muted hover:bg-surface-sunken hover:text-text-primary"
          :class="{ 'bg-surface-sunken text-text-primary': activeStates.orderedList }"
          :title="$t('W: Nummereret liste')"
          @click="editor?.chain().focus().toggleOrderedList().run()">
          <IconLucideListOrdered class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-border-subtle" />
        <button
          type="button"
          class="rounded p-1.5 text-text-muted hover:bg-surface-sunken hover:text-text-primary"
          :title="$t('W: Linjeskift (Shift + Enter)')"
          @click="editor?.chain().focus().setHardBreak().run()">
          <IconLucideWrapText class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-gray-200" />

        <!-- Insert table button + grid picker -->
        <div class="relative z-20">
          <button
            type="button"
            class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
            :class="{ 'bg-gray-200 text-gray-900': activeStates.isInTable }"
            title="Indsæt tabel"
            @click="showTablePicker = !showTablePicker">
            <IconLucideTable class="h-4 w-4" />
          </button>
          <div
            v-if="showTablePicker"
            class="absolute left-0 top-full z-20 mt-1 rounded border border-gray-200 bg-white p-2 shadow-md">
            <div class="mb-1.5 text-center text-xs text-gray-500">
              {{ pickerHoverRow > 0 ? `${pickerHoverRow} × ${pickerHoverCol}` : 'Indsæt tabel' }}
            </div>
            <div class="flex flex-col gap-0.5" @mouseleave="pickerHoverRow = 0; pickerHoverCol = 0">
              <div v-for="row in 6" :key="row" class="flex gap-0.5">
                <button
                  v-for="col in 6"
                  :key="col"
                  type="button"
                  class="h-5 w-5 rounded border transition-colors"
                  :class="
                    row <= pickerHoverRow && col <= pickerHoverCol
                      ? 'border-blue-400 bg-blue-100'
                      : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-50'
                  "
                  @mouseenter="pickerHoverRow = row; pickerHoverCol = col"
                  @click="insertTable(row, col)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contextual table toolbar (shown when cursor is inside a table) -->
      <div
        v-if="activeStates.isInTable"
        class="flex items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-1.5 py-1">
        <!-- Row operations -->
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          title="Tilføj række ovenover"
          @click="editor?.chain().focus().addRowBefore().run()">
          <IconLucideBetweenHorizontalStart class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          title="Tilføj række nedenunder"
          @click="editor?.chain().focus().addRowAfter().run()">
          <IconLucideBetweenHorizontalEnd class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          title="Slet række"
          @click="editor?.chain().focus().deleteRow().run()">
          <IconLucideTrash2 class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-gray-200" />
        <!-- Column operations -->
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          title="Tilføj kolonne til venstre"
          @click="editor?.chain().focus().addColumnBefore().run()">
          <IconLucideBetweenVerticalStart class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          title="Tilføj kolonne til højre"
          @click="editor?.chain().focus().addColumnAfter().run()">
          <IconLucideBetweenVerticalEnd class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          title="Slet kolonne"
          @click="editor?.chain().focus().deleteColumn().run()">
          <IconLucideTrash2 class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-gray-200" />
        <!-- Merge / split cells -->
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-600"
          title="Sammenføj celler"
          :disabled="!activeStates.canMergeCells"
          @click="editor?.chain().focus().mergeCells().run()">
          <IconLucideTableCellsMerge class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-600"
          title="Opdel celle"
          :disabled="!activeStates.canSplitCell"
          @click="editor?.chain().focus().splitCell().run()">
          <IconLucideTableCellsSplit class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-gray-200" />
        <!-- Delete table -->
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          title="Slet tabel"
          @click="editor?.chain().focus().deleteTable().run()">
          <IconLucideTrash2 class="h-4 w-4" />
        </button>
        <div class="mx-1 w-px self-stretch bg-gray-200" />
        <!-- Text alignment -->
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          :class="{ 'bg-gray-200 text-gray-900': activeStates.alignLeft }"
          title="Venstrejuster"
          @click="editor?.chain().focus().setTextAlign('left').run()">
          <IconLucideAlignLeft class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          :class="{ 'bg-gray-200 text-gray-900': activeStates.alignCenter }"
          title="Centrer"
          @click="editor?.chain().focus().setTextAlign('center').run()">
          <IconLucideAlignCenter class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
          :class="{ 'bg-gray-200 text-gray-900': activeStates.alignRight }"
          title="Højrejuster"
          @click="editor?.chain().focus().setTextAlign('right').run()">
          <IconLucideAlignRight class="h-4 w-4" />
        </button>
        <!-- Cell vertical alignment + background color (data cells only, not headers) -->
        <template v-if="activeStates.isInTableCell">
          <div class="mx-1 w-px self-stretch bg-gray-200" />
          <button
            type="button"
            class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
            :class="{ 'bg-gray-200 text-gray-900': cellVerticalAlign === 'top' }"
            title="Juster tekst til toppen"
            @click="setCellVerticalAlign('top')">
            <IconLucideAlignVerticalJustifyStart class="h-4 w-4" />
          </button>
          <button
            type="button"
            class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
            :class="{ 'bg-gray-200 text-gray-900': cellVerticalAlign === 'middle' }"
            title="Juster tekst til midten"
            @click="setCellVerticalAlign('middle')">
            <IconLucideAlignVerticalJustifyCenter class="h-4 w-4" />
          </button>
          <button
            type="button"
            class="cursor-pointer rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
            :class="{ 'bg-gray-200 text-gray-900': cellVerticalAlign === 'bottom' }"
            title="Juster tekst til bunden"
            @click="setCellVerticalAlign('bottom')">
            <IconLucideAlignVerticalJustifyEnd class="h-4 w-4" />
          </button>
          <div class="mx-1 w-px self-stretch bg-gray-200" />
          <ColorPickerButton
            :modelValue="cellBackgroundColorID"
            @update:model-value="setCellBackgroundColor" />
        </template>
      </div>

      <!-- Editor content -->
      <div class="relative">
        <!-- eslint-disable-next-line @typescript-eslint/no-explicit-any -->
        <EditorContent :editor="(editor as any)" class="rich-text-editor" />
        <div
          v-if="isEmpty && placeholder"
          class="pointer-events-none absolute left-0 top-0 p-3 text-sm text-text-muted">
          {{ placeholder }}
        </div>
      </div>
    </div>

    <!-- Names the shortcut users don't know; Enter's own behaviour is obvious.
         Same treatment as an input's `hint` prop (.field-hint in base.css), spelled
         out as utilities because that rule is nested under .s-vee-form-field and the
         editor is not a Vee field. -->
    <p class="mt-2 text-sm text-text-secondary">
      {{ $t('W: Tryk Shift + Enter for at starte en ny linje uden afstand') }}
    </p>
  </div>
</template>

<script lang="ts">
import { markRaw } from 'vue'
import { debounce } from 'lodash-es'
import { Editor } from '@tiptap/core'
import { EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { TableKit } from '@tiptap/extension-table'
import { TextAlign } from '@tiptap/extension-text-align'
import { sanitizeRichText } from '@/globals/typescript/utils/sanitizeRichText'
import { CustomTable } from '@/globals/typescript/tiptap/CustomTable'
import { CustomTableCell } from '@/globals/typescript/tiptap/CustomTableCell'
import type { TCellVerticalAlign } from '@/globals/typescript/tiptap/CustomTableCell'
import type { TResultColorId } from '@team-milva/lab-models/resources'

export default {
  name: 'RichTextEditor',

  components: { EditorContent },

  emits: ['update:modelValue'],

  props: {
    modelValue: { type: String, required: true },
    placeholder: { type: String, default: '' },
  },

  data() {
    return {
      editor: undefined as Editor | undefined,
      activeStates: {
        bold: false,
        italic: false,
        heading: false,
        bulletList: false,
        orderedList: false,
        isInTable: false,
        isInTableCell: false,
        canMergeCells: false,
        canSplitCell: false,
        alignLeft: false,
        alignCenter: false,
        alignRight: false,
      },
      isEmpty: true,
      showTablePicker: false,
      pickerHoverRow: 0,
      pickerHoverCol: 0,
      cellBackgroundColorID: null as TResultColorId | null,
      cellVerticalAlign: 'top' as TCellVerticalAlign,
      debouncedSyncTableWidths: null as (() => void) | null,
    }
  },

  watch: {
    modelValue(value: string) {
      if (this.editor && value !== this.editor.getHTML()) {
        this.editor.commands.setContent(value)
      }
    },
  },

  methods: {
    syncActiveStates() {
      if (!this.editor) return
      this.activeStates.bold = this.editor.isActive('bold')
      this.activeStates.italic = this.editor.isActive('italic')
      this.activeStates.heading = this.editor.isActive('heading', { level: 2 })
      this.activeStates.bulletList = this.editor.isActive('bulletList')
      this.activeStates.orderedList = this.editor.isActive('orderedList')
      this.activeStates.isInTableCell = this.editor.isActive('tableCell')
      this.activeStates.isInTable = this.activeStates.isInTableCell || this.editor.isActive('tableHeader')
      this.activeStates.canMergeCells = this.editor.can().mergeCells()
      this.activeStates.canSplitCell = this.editor.can().splitCell()
      this.activeStates.alignLeft = this.editor.isActive({ textAlign: 'left' })
      this.activeStates.alignCenter = this.editor.isActive({ textAlign: 'center' })
      this.activeStates.alignRight = this.editor.isActive({ textAlign: 'right' })
      const doc = this.editor.state.doc
      this.isEmpty =
        doc.childCount === 1 &&
        doc.firstChild?.type.name === 'paragraph' &&
        doc.firstChild.childCount === 0
      if (this.activeStates.isInTableCell) {
        const attrs = this.editor.getAttributes('tableCell')
        this.cellBackgroundColorID = (attrs.backgroundColor as TResultColorId | null) ?? null
        this.cellVerticalAlign = (attrs.verticalAlign as TCellVerticalAlign | null) ?? 'top'
      } else {
        this.cellBackgroundColorID = null
        this.cellVerticalAlign = 'top'
      }
    },

    closeTablePicker() {
      this.showTablePicker = false
      this.pickerHoverRow = 0
      this.pickerHoverCol = 0
    },

    // The editor's own usable content width in px — the reference frame both
    // insertTable() (writing starting column widths) and syncTableWidths()
    // (measuring how those widths render) size tables against.
    getEditorContentWidth(): number {
      const editorEl = this.editor?.view.dom as HTMLElement | undefined
      if (!editorEl) return 0
      const editorStyle = getComputedStyle(editorEl)
      return (
        editorEl.clientWidth -
        parseFloat(editorStyle.paddingLeft) -
        parseFloat(editorStyle.paddingRight)
      )
    },

    // Measures each table's real rendered width against the editor's own content
    // width and stores the ratio as a percentage on the table node. This is what
    // lets the real-report/PDF preview render the table at its authored width
    // instead of always stretching it to 100% — see normalizeTableColumnWidths()
    // in @team-milva/lab-pdfs, which reads this back out.
    //
    // Column widths are always absolute pixels (that's how ProseMirror's resize
    // handles work), and table-layout: fixed lets their sum win over max-width —
    // so a table can end up rendering wider than the editor's content area (after
    // adding a column, dragging one wider, or viewing a table authored at a wider
    // content width than the current one). Whenever that happens, every column is
    // rescaled proportionally to fit back within the content area — this is what
    // keeps the table's own max width capped at the editor's content width.
    syncTableWidths() {
      const editor = this.editor
      if (!editor) return
      const contentWidth = this.getEditorContentWidth()
      if (contentWidth <= 0) return

      const percentUpdates: { pos: number; percent: number }[] = []
      const rescales: { pos: number; scale: number }[] = []

      editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== 'table') return true
        // nodeDOM(pos) returns the .tableWrapper (TableView's root element),
        // which always stretches to fill its container — the actual <table>
        // nested inside it is the one that renders at its authored width.
        const wrapper = editor.view.nodeDOM(pos) as HTMLElement | null
        const dom = wrapper?.matches('table') ? wrapper : wrapper?.querySelector('table')
        if (!dom) return true
        const renderedWidth = dom.getBoundingClientRect().width

        if (renderedWidth > contentWidth + 0.5) {
          rescales.push({ pos, scale: contentWidth / renderedWidth })
          return true
        }

        const storedPercent = (node.attrs.widthPercent as number | null) ?? 100
        const measuredPercent = Math.min(100, Math.round((renderedWidth / contentWidth) * 100))
        if (Math.abs(measuredPercent - storedPercent) >= 1) {
          percentUpdates.push({ pos, percent: measuredPercent })
        }
        return true
      })
      if (!rescales.length && !percentUpdates.length) return

      editor.commands.command(({ tr }) => {
        rescales.forEach(({ pos, scale }) => {
          const tableNode = tr.doc.nodeAt(pos)
          if (!tableNode) return
          tr.doc.nodesBetween(pos, pos + tableNode.nodeSize, (cellNode, cellPos) => {
            if (cellNode.type.name !== 'tableCell') return true
            const colwidth = cellNode.attrs.colwidth as number[] | null
            if (!colwidth) return true
            const scaled = colwidth.map((w) => Math.max(25, Math.round(w * scale)))
            tr.setNodeMarkup(cellPos, undefined, { ...cellNode.attrs, colwidth: scaled })
            return true
          })
        })
        percentUpdates.forEach(({ pos, percent }) => {
          const node = tr.doc.nodeAt(pos)
          if (node) tr.setNodeMarkup(pos, undefined, { ...node.attrs, widthPercent: percent })
        })
        return true
      })
    },

    insertTable(rows: number, cols: number) {
      const editor = this.editor
      if (!editor) return
      // Give every column an explicit starting pixel width summing to the editor's
      // current content width — since the table CSS no longer forces width: 100%
      // (see the .ProseMirror table rule below), this is what makes a fresh table
      // start out at full width via real, resizable column widths rather than a
      // CSS override. Without this, a fresh table would render shrink-to-content.
      // Reserves a couple of px for border-collapse rendering overhead — without
      // it, columns summing exactly to contentWidth still render ~1px wider than
      // the container (the shared outer border), triggering a visible scrollbar
      // on a table that was never actually resized.
      const contentWidth = this.getEditorContentWidth()
      const colWidth = contentWidth > 0 ? Math.floor((contentWidth - 2) / cols) : null
      editor
        .chain()
        .focus()
        .insertTable({ rows, cols, withHeaderRow: false })
        // Freshly inserted cells default to vertical-align: middle. Existing/pasted
        // content is never touched here, so it keeps rendering via the report CSS's
        // own default (top) — see the comment on CustomTableCell's verticalAlign attr.
        .command(({ tr, state }) => {
          const { $from } = state.selection
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth)
            if (node.type.name !== 'table') continue
            const start = $from.before(depth)
            state.doc.nodesBetween(start, start + node.nodeSize, (cellNode, cellPos) => {
              if (cellNode.type.name === 'tableCell') {
                tr.setNodeMarkup(cellPos, undefined, {
                  ...cellNode.attrs,
                  verticalAlign: 'middle',
                  ...(colWidth ? { colwidth: [colWidth] } : {}),
                })
              }
            })
            break
          }
          return true
        })
        .run()
      this.closeTablePicker()
    },

    setCellBackgroundColor(colorId: TResultColorId | null): void {
      this.editor?.chain().focus().updateAttributes('tableCell', { backgroundColor: colorId }).run()
    },

    setCellVerticalAlign(align: TCellVerticalAlign): void {
      this.editor?.chain().focus().updateAttributes('tableCell', { verticalAlign: align }).run()
    },
  },

  mounted() {
    const editor = markRaw(new Editor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [2] },
          blockquote: false,
          code: false,
          codeBlock: false,
          horizontalRule: false,
          strike: false,
        }),
        TableKit.configure({ table: false, tableCell: false }),
        CustomTable.configure({ resizable: true }),
        CustomTableCell,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'tableCell', 'tableHeader'],
        }),
      ],
      content: this.modelValue,
      editorProps: {
        transformPastedHTML: (html: string) => sanitizeRichText(html),
      },
    })) as unknown as Editor
    this.editor = editor
    editor.on('update', () => {
      this.syncTableWidths()
      const html = this.editor?.getHTML() ?? ''
      this.$emit('update:modelValue', html)
      this.syncActiveStates()
    })
    editor.on('selectionUpdate', () => {
      this.syncActiveStates()
    })
    this.syncActiveStates()
    this.syncTableWidths()
    // Catches a live window/layout resize mid-session too, not just the
    // mount/edit cycles 'update' already covers.
    this.debouncedSyncTableWidths = debounce(() => this.syncTableWidths(), 200)
    window.addEventListener('resize', this.debouncedSyncTableWidths)
  },

  beforeUnmount() {
    this.closeTablePicker()
    if (this.debouncedSyncTableWidths) {
      window.removeEventListener('resize', this.debouncedSyncTableWidths)
    }
    this.editor?.destroy()
  },
}
</script>

<style scoped>
.rich-text-editor :deep(.ProseMirror) {
  min-height: 96px;
  padding: 8px 12px;
  font-size: 0.875rem;
  line-height: 1.5rem;
  outline: none;
  background-color: white;
}

.rich-text-editor :deep(.ProseMirror h2) {
  font-size: 1rem;
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 4px;
}

.rich-text-editor :deep(.ProseMirror h2:first-child) {
  margin-top: 0;
}

/* Matches the paragraph gap the report/PDF uses (labBase1.styles.ts), so a
   paragraph break in the editor looks like it will in the report — and stays
   clearly distinguishable from a Shift+Enter line break, which has no gap. */
.rich-text-editor :deep(.ProseMirror p) {
  margin-bottom: 0.8em;
}

.rich-text-editor :deep(.ProseMirror p:last-child) {
  margin-bottom: 0;
}

.rich-text-editor :deep(.ProseMirror ul),
.rich-text-editor :deep(.ProseMirror ol) {
  padding-left: 20px;
  margin: 4px 0;
}

.rich-text-editor :deep(.ProseMirror ul) {
  list-style-type: disc;
}

.rich-text-editor :deep(.ProseMirror ol) {
  list-style-type: decimal;
}

.rich-text-editor :deep(.ProseMirror li) {
  margin-bottom: 2px;
}

/* Table styles */
.rich-text-editor :deep(.ProseMirror .tableWrapper) {
  overflow-x: auto;
  margin: 8px 0;
}

.rich-text-editor :deep(.ProseMirror table) {
  border-collapse: collapse;
  table-layout: fixed;
  /* Sizes to the sum of its columns' own widths (set explicitly on insert, see
     RichTextEditor's insertTable()) rather than always stretching to fill —
     this is what lets a table be genuinely authored narrower than full width. */
  width: auto;
  max-width: 100%;
  overflow: hidden;
}

.rich-text-editor :deep(.ProseMirror th),
.rich-text-editor :deep(.ProseMirror td) {
  border: 1px solid #d1d5db;
  box-sizing: border-box;
  min-width: 1em;
  padding: 6px 8px;
  position: relative;
  vertical-align: top;
}


.rich-text-editor :deep(.ProseMirror td p),
.rich-text-editor :deep(.ProseMirror th p) {
  margin-bottom: 0;
}

.rich-text-editor :deep(.ProseMirror .column-resize-handle) {
  background-color: #818cf8;
  bottom: -2px;
  pointer-events: none;
  position: absolute;
  right: -2px;
  top: 0;
  width: 4px;
}

.rich-text-editor :deep(.ProseMirror .selectedCell::after) {
  background: rgba(99, 102, 241, 0.1);
  content: '';
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 2;
}

.rich-text-editor :deep(.resize-cursor) {
  cursor: col-resize;
}
</style>
